import React, { useCallback, memo } from 'react';
import styled from 'styled-components';
import { get } from 'lodash';

import InfiniteList from '../../../InfiniteList';
import Button from '../../../Button';
import { SEARCH_CARD_TYPES } from '../../../../constants';
import ClientSearchCard from '../../Clients/ClientSearchCard';
import OrderSearchCard from '../../Orders/OrderSearchCard';
import CommunicationSearchCard from '../../Logs/CommunicationSearchCard';
import Throbber from '../../../Throbber';
import {
  IClientSearchInfo,
  ICommunicationSearchInfo,
  INoteSearchInfo,
  IOrderSearchInfo,
  ISearchCardActions,
  SearchCardType,
} from '../types';

const ITEM_HEIGHT = 44;
const PAGE_SIZE_DEFAULT = 20;
const THRESHOLD_DEFAULT = 5;
const getItemKey = (index, data) => get(data, `${index}.id`) || index;

const TITLE_COMPONENT = {
  None: '',
  [SEARCH_CARD_TYPES.CLIENTS]: 'клиенты',
  [SEARCH_CARD_TYPES.ORDERS]: 'заказы',
  [SEARCH_CARD_TYPES.COMMUNICATION]: 'коммуникации',
  [SEARCH_CARD_TYPES.NOTES]: 'заметки',
  [SEARCH_CARD_TYPES.MESSAGES]: 'сообщения',
};

interface IListSearchCard {
  className?: string;
  displayCount?: number;
  hasNextPage?: boolean;
  isLoading?: boolean;
  items: Array<
    | IOrderSearchInfo
    | IClientSearchInfo
    | ICommunicationSearchInfo
    | INoteSearchInfo
  >;
  minimumBatchSize?: number;
  open?: boolean;
  threshold?: number;
  total: number;
  type: SearchCardType;
  action: {
    onToggle: ISearchCardActions['onToggle'];
    onLoadRequest: ISearchCardActions['onLoadRequest'];
    onCallClick: ISearchCardActions['onCallClick'];
    onEmailClick: ISearchCardActions['onEmailClick'];
    onCreateTicket: ISearchCardActions['onCreateTicket'];
    onCardClick: ISearchCardActions['onCardClick'];
  };
}

const ListSearchCard = ({
  total,
  type,
  open = false,
  hasNextPage = false,
  displayCount = 3,
  minimumBatchSize = PAGE_SIZE_DEFAULT,
  threshold = THRESHOLD_DEFAULT,
  items,
  action = {
    onToggle: type => null,
    onLoadRequest: (startIndex, stopIndex) => null,
    onCallClick: () => null,
    onEmailClick: () => null,
    onCreateTicket: () => null,
    onCardClick: ({ type, item }) => null,
  },
  isLoading = false,
  ...props
}: IListSearchCard) => {
  const { onToggle, onLoadRequest, onCardClick, ...otherAction } = action;

  if (!type) {
    return null;
  }

  const handleToggle = () => onToggle(type);
  const isItemLoaded = index => !hasNextPage || index < items.length;
  const itemCount = hasNextPage ? items.length + 1 : items.length;
  const handleLoadPage = (startIndex, stopIndex) => {
    if (isLoading) {
      return;
    }

    onLoadRequest(startIndex, stopIndex);
  };

  const handleCardClick = useCallback(({ type, item }) => {
    onCardClick({ type, item });
  }, []);

  const count = Math.min(total, displayCount, items.length);
  let buttonTitle = '';
  let Component;
  let list = items;

  if (!open && count !== items.length) {
    list = items.slice(0, count);
  }

  if (count !== items.length) {
    buttonTitle = open ? 'Свернуть' : 'Показать все';
  }

  switch (type) {
    case SEARCH_CARD_TYPES.CLIENTS:
      Component = StyledClientsSearchCard;
      break;
    case SEARCH_CARD_TYPES.ORDERS:
      Component = StyledOrderSearchCard;
      break;
    case SEARCH_CARD_TYPES.COMMUNICATION:
    case SEARCH_CARD_TYPES.NOTES:
    case SEARCH_CARD_TYPES.MESSAGES:
      Component = StyledCommunicationSearchCard;
      break;
    default:
      Component = null;
  }

  return (
    <StyledListSearchCard {...props}>
      <StyledTitelHeader>
        <StyledTitle>
          {TITLE_COMPONENT[type]}
          <span> • </span>
          {total}
        </StyledTitle>
        {total > 0 && buttonTitle && (
          <StyledButton onClick={handleToggle}>{buttonTitle}</StyledButton>
        )}
      </StyledTitelHeader>
      <StyledContainer open={open}>
        {!open && (
          <>
            {!isLoading ? (
              list.map((item, id) => (
                <Component
                  {...item}
                  disabled={item.disabled}
                  key={id}
                  onClick={() => handleCardClick({ type, item })}
                  {...otherAction}
                />
              ))
            ) : (
              <StyledThrobberContainer>
                <Throbber color="#007aff" />
              </StyledThrobberContainer>
            )}
          </>
        )}

        {open && (
          <InfiniteList
            {...props}
            itemData={list}
            itemKey={getItemKey}
            itemSize={ITEM_HEIGHT}
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            minimumBatchSize={minimumBatchSize}
            onLoadRequest={handleLoadPage}
            threshold={threshold}
          >
            {({ data, index, style }) => {
              if (!isItemLoaded(index)) {
                return (
                  <StyledLoadingCommunication style={style}>
                    {' '}
                    Loading...
                  </StyledLoadingCommunication>
                );
              }

              const item = get(data, `${index}`);
              if (!item) {
                return null;
              }
              const newStyle = index ? style : { ...style };

              return (
                <Component
                  style={{
                    ...newStyle,
                    height: ITEM_HEIGHT,
                  }}
                  {...item}
                  {...otherAction}
                  onClick={() => handleCardClick({ type, item })}
                />
              );
            }}
          </InfiniteList>
        )}
      </StyledContainer>
    </StyledListSearchCard>
  );
};

const StyledListSearchCard = styled.div`
  background-color: #fff;
  height: 100%;
  width: 100%;
  position: relative;
`;

const StyledTitelHeader = styled.div`
  width: 100%;
  font-size: 14px;
  line-height: 18px;
  height: 40px;
  color: #17181a;
  display: flex;
  padding-left: 20px;
  padding-right: 11px;
  padding-top: 16px;
`;

const StyledTitle = styled.div`
  font-weight: bold;
  letter-spacing: 1.9px;
  text-transform: uppercase;
`;

const StyledButton = styled(Button)`
  width: 90px;
  height: 18px;
  font-size: 14px;
  padding: 0px;
  margin-left: 7px;
  line-height: 1.29;
  letter-spacing: 0.1px;
  border: none;
`;

const StyledContainer = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  height: 100%;

  ${props =>
    props.open &&
    `
    height: 264px;
  `}

  ${props =>
    !props.open &&
    `
    padding-bottom: 25px;
  `}
`;

const StyledClientsSearchCard = styled(ClientSearchCard)<{ disabled: boolean }>`
  padding: 13px 20px;
  border-top: 1px solid #e9ebee;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  :hover {
    background-color: #dcedff;
  }
`;

const StyledOrderSearchCard = styled(OrderSearchCard)`
  padding: 0 20px;
  border-top: 1px solid #e9ebee;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  :hover {
    background-color: #dcedff;
  }
`;

const StyledCommunicationSearchCard = styled(CommunicationSearchCard)<{
  disabled: boolean;
}>`
  padding: 0 20px;
  border-top: 1px solid #e9ebee;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  :hover {
    background-color: #dcedff;
  }
`;

const StyledLoadingCommunication = styled.div`
  align-items: center;
  height: ${ITEM_HEIGHT}px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledThrobberContainer = styled.div`
  margin-top: 15px;
  height: 110px;
`;

export default memo(ListSearchCard);
